import { useRef } from 'react';
import StatusPill from './StatusPill.jsx';
import { Drawer } from './ui';
import HelpPopover from './ui/HelpPopover.jsx';
import SalaryPanel from './SalaryPanel.jsx';
import NextEventPanel from './NextEventPanel.jsx';
import { timeAgoFine, formatDateTime } from '../util/time.js';

// T130 — one right-side drawer per job, consolidating what was scattered across
// the expand-row, the ScoreBadge popover, the KebabMenu, and four AI modals:
// full description, full score breakdown, AI bullets, artifact freshness, and
// every action. Composition only — reuses the existing rec payload + the page's
// action handlers/modals; computes nothing new, adds no backend.
//
// Shell intentionally mirrors ApplicationsPage's SideDrawer (z-40, right-side,
// border-l) so T131 can fold both into one shared <Drawer> primitive.
//
// childModalOpen: while an AI modal (z-50) is mounted OVER the drawer, the
// drawer's Escape early-returns so one keypress closes only the modal, not both.

// Mirrors ScoreBadge's ScorePopover Row (T131: promote to a shared ScoreBreakdown).
function Dim({ label, value, muted, note }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className={muted ? 'text-slate-500' : 'text-slate-300'}>
        {label}
        {note && <span className="block text-xs text-slate-500">{note}</span>}
      </dt>
      <dd className={'font-mono ' + (muted ? 'text-slate-500' : 'text-slate-100')}>{value}</dd>
    </div>
  );
}

// Existence + recency of an AI artifact. Color is always paired with text
// (principle: color never alone); relative time inline, absolute in title.
function ArtifactChip({ label, ready, at, onOpen }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      title={at ? formatDateTime(at) : undefined}
      className={
        'flex min-h-[44px] flex-col items-start justify-center gap-0.5 rounded-lg border px-3 py-2 text-left text-xs transition ' +
        (ready
          ? 'border-emerald-700 bg-emerald-950/20 text-emerald-200 hover:border-emerald-600'
          : 'border-slate-700 text-slate-400 hover:border-brand-600 hover:text-brand-300')
      }
    >
      <span className="font-medium">{label}</span>
      <span className={ready ? 'text-emerald-400' : 'text-slate-500'}>
        {ready ? `✓ ${timeAgoFine(at)}` : 'not yet'}
      </span>
    </button>
  );
}

export default function JobDrawer({
  rec,
  onRecPatched,
  childModalOpen,
  onClose,
  onUpdate,
  onExplain,
  onRequestDismiss,
  pending,
  explainPending,
  onOpenCoverLetter,
  onOpenTailoredCv,
  onOpenInterviewPrep,
  onOpenCompanyResearch,
}) {
  const closeRef = useRef(null);
  // T248 — the focus trap, the restore-to-opener and the gated Escape all moved
  // into <Drawer> below. The gate itself is unchanged and still load-bearing:
  // while a child AI modal (z-50) sits over the drawer it must own Escape, or
  // one keypress closes both.

  const job = rec?.job;
  if (!job) return null;

  const shortlisted = rec.status === 'shortlisted';
  const exp = rec.llmExplanation;
  const bd = rec.scoreBreakdown;
  const busy = pending === rec.id;
  const explaining = explainPending === rec.id;
  const desc = job.description ?? '';
  const showSemantic = bd?.semantic !== null && bd?.semantic !== undefined;
  const showLlm = bd?.llm !== null && bd?.llm !== undefined;
  const showNeg = typeof bd?.negative === 'number' && bd.negative > 0;
  const locality = [job.location?.city, job.location?.country].filter(Boolean).join(', ');

  return (
    // T248 — folded into ui/Drawer, which this file's own comment promised.
    // NOTE the honest scope: unlike the other four overlays, this one was
    // already accessible — it had the trap and the gated Escape. What it
    // duplicated was the scrim + panel scaffolding, and duplication is how the
    // two drawers drifted apart in the first place.
    <Drawer
      onClose={onClose}
      tone="drawer"
      side="right"
      panelClassName="w-full max-w-lg"
      labelledById="job-drawer-title"
      initialFocusRef={closeRef}
      escapeClosesOnTop={!childModalOpen}
    >
      <>
        <header className="flex items-start justify-between gap-3 border-b border-slate-800 p-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {rec.status !== 'new' ? (
                <StatusPill compact status={rec.status} />
              ) : (
                <span className="text-[10px] uppercase tracking-wide text-slate-500">New</span>
              )}
              <span className="font-mono text-sm text-brand-400">{rec.scoreTotal ?? 0}</span>
            </div>
            <h2 id="job-drawer-title" className="mt-1 text-base font-semibold leading-snug text-slate-100">
              {job.title}
            </h2>
            <p className="truncate text-xs text-slate-400">
              {job.company}
              {locality && ` · ${locality}`}
              {job.location?.remote && job.location.remote !== 'unknown' && ` · ${job.location.remote}`}
            </p>
            <p className="mt-1 flex flex-wrap items-center gap-2 text-[10px] text-slate-500">
              <code className="font-mono">{job.source}</code>
              {job.firstSeenAt && (
                <span title={formatDateTime(job.firstSeenAt)}>added {timeAgoFine(job.firstSeenAt)}</span>
              )}
              <a
                href={job.url}
                target="_blank"
                rel="noreferrer"
                className="text-brand-400 underline decoration-dotted hover:text-brand-300"
              >
                open posting ↗
              </a>
            </p>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 rounded-md border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:border-slate-500 hover:text-slate-200"
          >
            ✕
          </button>
        </header>

        <div className="flex-1 space-y-5 overflow-y-auto p-4">
          {/* Score breakdown — mirrors ScoreBadge ScorePopover (T131: shared ScoreBreakdown) */}
          <section>
            <h3 className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500">
              Score breakdown
              <HelpPopover label="How the score is built" title="How the score is built" side="left" width="w-64">
                <strong>Rules</strong> match your prefs (location, skills, salary).{' '}
                <strong>Semantic</strong> compares your profile embedding to the job.{' '}
                <strong>AI fit signal</strong> is Claude&apos;s read (run Explain to compute). A{' '}
                <strong>dismissed-similar penalty</strong> subtracts points when a role resembles one
                you dismissed.
              </HelpPopover>
            </h3>
            <dl className="mt-2 space-y-1 text-xs">
              <Dim label="Rules (location, skills, salary…)" value={bd?.rules ?? 0} />
              {showSemantic ? (
                <Dim label="Semantic (profile ↔ job)" value={bd.semantic} />
              ) : (
                <Dim label="Semantic (profile ↔ job)" value="—" muted note="computed on next scan" />
              )}
              {showLlm ? (
                <Dim label="AI fit signal" value={bd.llm} />
              ) : (
                <Dim label="AI fit signal" value="—" muted note="run Explain to compute" />
              )}
              {showNeg && (
                <Dim label="Dismissed-similar penalty" value={`−${bd.negative}`} note="similar to a role you dismissed" />
              )}
            </dl>
            {rec.reasons?.length > 0 && (
              <div className="mt-3">
                <p className="text-[10px] uppercase tracking-widest text-emerald-400">Why it matched</p>
                <ul className="mt-1 space-y-0.5 text-xs">
                  {rec.reasons.map((r, i) => (
                    <li key={i} className="flex items-start gap-1 text-emerald-200">
                      <span className="shrink-0">✓</span>
                      <span className="leading-snug">{r}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {rec.risks?.length > 0 && (
              <div className="mt-3">
                <p className="text-[10px] uppercase tracking-widest text-amber-400">Risks / gaps</p>
                <ul className="mt-1 space-y-0.5 text-xs">
                  {rec.risks.map((r, i) => (
                    <li key={i} className="flex items-start gap-1 text-amber-200">
                      <span className="shrink-0">⚠</span>
                      <span className="leading-snug">{r}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {rec.missingSkills?.length > 0 && (
              <div className="mt-3">
                <p className="text-[10px] uppercase tracking-widest text-slate-400">Missing skills</p>
                <p className="mt-1 text-xs leading-snug text-slate-300">{rec.missingSkills.join(', ')}</p>
              </div>
            )}
            {/* T189 — what the ad said vs what the operator expects, kept apart. */}
            <SalaryPanel rec={rec} onUpdated={onRecPatched} />
            {/* T322 — the one date that points forward. Everything else on a
                recommendation records what already happened. */}
            <NextEventPanel rec={rec} onUpdated={onRecPatched} />
          </section>

          {/* AI explanation bullets */}
          <section>
            <div className="flex items-center justify-between">
              <h3 className="text-[10px] uppercase tracking-wide text-brand-500">✨ AI</h3>
              {!exp && (
                <button
                  type="button"
                  onClick={() => onExplain?.(rec.id)}
                  disabled={explaining}
                  className="rounded border border-brand-600 px-2 py-0.5 text-[10px] font-medium text-brand-300 transition hover:bg-brand-600/10 disabled:opacity-50"
                >
                  {explaining ? '✨ Explaining…' : '✨ Explain match'}
                </button>
              )}
            </div>
            {exp ? (
              <div className="mt-2 space-y-2 rounded-lg border border-brand-700 bg-brand-600/5 p-3 text-xs">
                {exp.whyMatch?.length > 0 && (
                  <div className="space-y-0.5">
                    {exp.whyMatch.map((b, i) => (
                      <div key={`w${i}`} className="text-emerald-300">+ {b}</div>
                    ))}
                  </div>
                )}
                {exp.whatsMissing?.length > 0 && (
                  <div className="space-y-0.5">
                    {exp.whatsMissing.map((b, i) => (
                      <div key={`m${i}`} className="text-amber-300">– {b}</div>
                    ))}
                  </div>
                )}
                {/* T256(7) — the field is an array (the dashboard row joins it
                    with " · "); rendering it bare let React concatenate the
                    entries with no separator at all: "ReactNode.jsKubernetes". */}
                {(Array.isArray(exp.emphasizeInCv) ? exp.emphasizeInCv.length > 0 : Boolean(exp.emphasizeInCv)) && (
                  <p className="text-slate-300">
                    <span className="text-slate-500">Emphasize in CV: </span>
                    {Array.isArray(exp.emphasizeInCv) ? exp.emphasizeInCv.join(' · ') : exp.emphasizeInCv}
                  </p>
                )}
                {exp.interviewQuestion && (
                  <p className="text-slate-300">
                    <span className="text-slate-500">Likely question: </span>
                    {exp.interviewQuestion}
                  </p>
                )}
                {exp.riskFlags?.length > 0 && (
                  <div className="space-y-0.5">
                    {exp.riskFlags.map((b, i) => (
                      <div key={`f${i}`} className="text-amber-300">⚠ {b}</div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <p className="mt-2 text-xs text-slate-500">
                No AI explanation yet — generate one to see why this matched, what's missing, and what to emphasize.
              </p>
            )}
          </section>

          {/* AI artifacts (freshness + entry points) */}
          <section>
            <h3 className="text-[10px] uppercase tracking-widest text-slate-500">AI artifacts</h3>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <ArtifactChip
                label="Cover letter"
                ready={!!rec.coverLetter}
                at={rec.coverLetter?.editedAt ?? rec.coverLetter?.generatedAt}
                onOpen={() => onOpenCoverLetter?.(rec)}
              />
              <ArtifactChip
                label="Tailored CV"
                ready={!!rec.tailoredCv}
                at={rec.tailoredCv?.editedAt ?? rec.tailoredCv?.generatedAt}
                onOpen={() => onOpenTailoredCv?.(rec)}
              />
              <ArtifactChip
                label="Interview prep"
                ready={!!rec.interviewPrep}
                at={rec.interviewPrep?.generatedAt}
                onOpen={() => onOpenInterviewPrep?.(rec)}
              />
              {/* Company research isn't stored on the rec (keyed by company name),
                  so it can't carry freshness — it's a plain entry point. */}
              <button
                type="button"
                onClick={() => onOpenCompanyResearch?.(rec)}
                className="flex min-h-[44px] flex-col items-start justify-center gap-0.5 rounded-lg border border-slate-700 px-3 py-2 text-left text-xs text-slate-400 transition hover:border-brand-600 hover:text-brand-300"
              >
                <span className="font-medium">Company research</span>
                <span className="truncate text-slate-500">🏢 {job.company}</span>
              </button>
            </div>
          </section>

          {/* Full job description */}
          <section>
            <h3 className="text-[10px] uppercase tracking-widest text-slate-500">Job description</h3>
            {desc ? (
              <div className="mt-2 whitespace-pre-wrap rounded-lg border border-slate-800 bg-slate-950/40 p-3 text-xs leading-relaxed text-slate-300">
                {desc}
              </div>
            ) : (
              <p className="mt-2 text-xs text-slate-500">
                No description captured for this posting.{' '}
                <a
                  href={job.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-brand-400 underline decoration-dotted hover:text-brand-300"
                >
                  Open the original ↗
                </a>
              </p>
            )}
          </section>
        </div>

        {/* Sticky action footer (≥44px touch targets) */}
        <footer className="flex items-center gap-2 border-t border-slate-800 bg-slate-900/80 p-3">
          <button
            type="button"
            disabled={busy}
            onClick={() => onUpdate?.(rec.id, shortlisted ? 'new' : 'shortlisted')}
            className="min-h-[44px] flex-1 rounded-md border border-slate-700 px-3 py-2 text-xs font-medium text-slate-200 transition hover:border-brand-600 hover:text-brand-400 disabled:opacity-50"
          >
            {shortlisted ? '★ Shortlisted' : '☆ Shortlist'}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => onUpdate?.(rec.id, 'applied')}
            className="min-h-[44px] flex-1 rounded-md bg-brand-600 px-3 py-2 text-xs font-medium text-white transition hover:bg-brand-500 disabled:opacity-50"
          >
            Mark applied
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => onRequestDismiss?.(rec)}
            className="min-h-[44px] flex-1 rounded-md border border-slate-700 px-3 py-2 text-xs text-slate-300 transition hover:border-red-600 hover:text-red-400 disabled:opacity-50"
          >
            Dismiss
          </button>
        </footer>
      </>
    </Drawer>
  );
}
